import loadingAnimation from '../../loading-animation.vue';
import CheckboxFilter from './checkbox-filter.vue';
import ColorFilter from './color-filter.vue';
import PriceFilter from './price-filter.vue';
import { nextTick } from 'vue';

export default {
  name: 'CollectionFilters',
  components: {
    LoadingAnimation: loadingAnimation,
    CheckboxFilter,
    ColorFilter,
    PriceFilter,
  },
  props: [
    'collectionId',
    'products',
    'minPrice',
    'maxPrice',
    'priceFilterIsActive',
    'initialFilters',
  ],
  data() {
    return {
      isLoading: true,
      filters: [],
      activeFilterDrawer: null,
      productsFiltered: false,
      priceRange: [this.minPrice, this.maxPrice],
      initialPriceRange: [this.minPrice, this.maxPrice],
    };
  },
  emits: [
    'options-change',
    'drawer-close',
    'products-change',
    'update-price-filter',
  ],
  computed: {
    selectedOptions() {
      return this.filters.flatMap((filter) => {
        return filter.options
          .filter((option) => option.selected)
          .map((option) => {
            return {
              ...option,
              filterId: filter.filterId,
              filterType: filter.filterType,
              title: filter.title,
            };
          });
      });
    },
    // check if filters are active or not
    filtersActive() {
      // returns true if any options are selected on any filter
      // calc if price filter is active
      const priceFilterActive =
        this.priceRange[0] > this.minPrice ||
        this.priceRange[1] < this.maxPrice;
      return this.selectedOptions.length > 0 || priceFilterActive;
    },
    // actually filter the products here
    filteredProducts() {
      // emit the new selected options
      this.$emit('options-change', this.selectedOptions);

      // get a new copy of the products array
      let filteredProducts = this.products.map((p) => ({ ...p }));

      // only need to pay attention to a filter if it has selected options
      const activeFilters = this.filters.filter((filter) =>
        filter.options.some((option) => option.selected)
      );
      // run through filters with any selected options
      for (const filter of activeFilters) {
        // get selected option values for this filter
        const optionValues = filter.options
          .filter((option) => option.selected)
          .map((option) => option.value);
        filteredProducts = filteredProducts.filter((product) => {
          // get property of product we care about
          let property;
          switch (filter.targetLevel) {
            case 'product':
              property = product[filter.targetField];
              // if property is an array ('tags', for eg), need to see if any of the values match any of the options
              if (Array.isArray(property)) {
                // compare property array against array of option values
                return property.some((val) => optionValues.includes(val));
              } else {
                // if a single value, compare it against array of option values
                return optionValues.includes(property);
              }
            case 'colorway':
              // if any of the colorways have a matching property, return true
              return product.colorways.some((colorway) =>
                optionValues.includes(colorway[filter.targetField])
              );
            case 'size':
              return product.colorways.some((colorway) =>
                colorway.sizes.some(
                  (size) =>
                    optionValues.includes(size[filter.targetField]) &&
                    size.available
                )
              );
          }
        });

        // if filter is targeting the colorway level, we want to filter out any colorways that don't match the filter
        if (filter.targetLevel === 'colorway') {
          filteredProducts.forEach((product) => {
            product.colorways = product.colorways.filter((colorway) =>
              optionValues.includes(colorway[filter.targetField])
            );
          });
        }
        // if filter is targeting the size level, we want to filter out any colorways that don't have inventory in the size
        if (filter.targetLevel === 'size') {
          filteredProducts.forEach((product) => {
            product.colorways = product.colorways.filter((colorway) =>
              colorway.sizes.some(
                (size) =>
                  optionValues.includes(size[filter.targetField]) &&
                  size.available
              )
            );
          });
        }
      }
      // filter by price filter, if active
      if (this.priceFilterIsActive) {
        filteredProducts.forEach((product) => {
          product.colorways = product.colorways.filter(
            (colorway) =>
              colorway.price >= this.priceRange[0] &&
              colorway.price <= this.priceRange[1]
          );
        });
      }

      // make sure to remove any products that don't have any colorways
      filteredProducts = filteredProducts.filter(
        (product) => product.colorways.length > 0
      );
      return filteredProducts;
    },
  },
  watch: {
    filteredProducts: {
      deep: true,
      handler: function (newFilteredProducts) {
        this.$emit('products-change', newFilteredProducts);
      },
    },
    selectedOptions: {
      deep: true,
      handler: function (newFilters) {
        this.updateUrlState();
      },
    },
    priceRange: {
      deep: true,
      handler: function (newPriceRange) {
        this.updateUrlState();
      },
    },
    // when filters are loaded from the parent component, create a copy here that
    // we can modify so we aren't trying to modify something from props
    initialFilters: {
      deep: true,
      handler: function (newFilters) {
        // we need to parse and stringify here to create a deep copy,
        // for some reason newFilters.map((f) => ({...f})) doesn't work
        this.filters = JSON.parse(JSON.stringify(newFilters));
        this.isLoading = false;
        // select any filter options from url params
        this.loadFiltersFromUrl();
      },
    },
  },
  methods: {
    // unselect filter option from parent component
    unselectOption(option) {
      // find option
      for (const filter of this.filters) {
        for (const filterOption of filter.options) {
          if (
            filter.filterId === option.filterId &&
            filterOption.value === option.value
          ) {
            // unselect it
            filterOption.selected = false;
            break;
          }
        }
      }
    },
    // uncheck all checkboxes
    clearFilters() {
      for (const filter of this.filters) {
        for (const option of filter.options) {
          option.selected = false;
        }
      }
      //reset price filter
      this.resetPriceFilter();
      this.closeDrawer();
    },
    // close filter drawer, if on mobile
    closeDrawer() {
      this.$emit('drawer-close');
    },
    // activate the accordion drawer
    openFilter(filterId) {
      // if this drawer is already open, close it
      if (this.activeFilterDrawer === filterId) {
        this.activeFilterDrawer = null;
      } else {
        // otherwise, open the drawer
        this.activeFilterDrawer = filterId;
      }
    },
    updatePriceRange(data) {
      this.priceRange = data;
      this.$emit('update-price-filter', data);
    },
    resetPriceFilter() {
      // priceFilter ref is actually an array since the <PriceFilter />
      // component is rendered inside of a v-for loop
      this.$refs.priceFilter[0].resetFilter();
    },
    // update query paramters in URL with current filter options
    async updateUrlState() {
      //get all the current url params - may include some UTM campaign or DY params we want to keep
      const urlParams = new URLSearchParams(window.location.search);

      //list out all the filter titles that we want to check url params against
      let activeFilters = this.filters.map((filter) => filter.title);

      //if there are no selected filters, clean up any possible filter params we added
      if (this.selectedOptions.length === 0) {
        for (const filter of activeFilters) {
          if (urlParams.has(filter)) {
            urlParams.delete(filter);
          }
        }
      }

      // wipe out any current filters so we don't duplicate
      for (const [key, value] of urlParams.entries()) {
        for (const param of activeFilters) {
          if (key === param) {
            urlParams.delete(key);
          }
        }
      }

      //then append normal selected filters
      for (const option of this.selectedOptions) {
        urlParams.append(option.title, option.value);
      }

      //gives us a sec for priceFilterIsActive computed property to update
      await nextTick();

      // set or append price range data for price filter
      if (this.priceFilterIsActive) {
        if (this.minPrice < this.priceRange[0]) {
          if (urlParams.has('minPrice')) {
            urlParams.set('minPrice', this.priceRange[0]);
          } else {
            urlParams.append('minPrice', this.priceRange[0]);
          }
        }
        if (this.maxPrice > this.priceRange[1]) {
          if (urlParams.has('maxPrice')) {
            urlParams.set('maxPrice', this.priceRange[1]);
          } else {
            urlParams.append('maxPrice', this.priceRange[1]);
          }
        }
      } else {
        //delete min and max price params if price filter is not active
        urlParams.delete('minPrice');
        urlParams.delete('maxPrice');
      }

      //update url with new params
      if (urlParams.size > 0) {
        window.history.replaceState(
          {},
          '',
          `${location.pathname}?${urlParams}`
        );
      } else {
        // if our url params are now empty, we can just update with no search params
        window.history.replaceState({}, '', location.pathname);
      }
    },
    // load filter settings from url paramters
    loadFiltersFromUrl() {
      // load selected filter options from url parameters
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      for (const [title, value] of urlParams) {
        // set price filter
        if (title === 'minPrice') {
          this.initialPriceRange[0] = Number(value);
        } else if (title === 'maxPrice') {
          this.initialPriceRange[1] = Number(value);
        } else if (title.toLowerCase() === 'tag') {
          // create hidden tag filter - for use by marketing
          // create option
          const tagOption = {
            label: `Tag: ${value}`,
            value: value,
            selected: true,
          };
          // see if tag "filter" already exists
          const tagFilter = this.filters.find(
            (filter) => filter.title === 'Tag'
          );
          // create a tag "filter"
          if (!tagFilter) {
            this.filters.push({
              filterId: 99999,
              name: 'Tag',
              title: 'Tag',
              filterType: 'hidden',
              targetLevel: 'product',
              targetField: 'tags',
              options: [tagOption],
            });
          } else {
            tagFilter.options.push(tagOption);
          }
        } else {
          // normal filter options
          const filter = this.filters.find((filter) => filter.title === title);
          if (filter) {
            const option = filter.options.find(
              (option) => option.value === value
            );
            if (option) option.selected = true;
          }
        }
      }
    },
  },
};
